import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import API from '../../utils/api';
import { useConfirm } from '../../components/ConfirmModal';

const AdminVoiceProfiles = () => {
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [total, setTotal]       = useState(0);
  const [pages, setPages]       = useState(1);
  const [loading, setLoading]   = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [msg, setMsg]           = useState('');

  const status = searchParams.get('status') || '';
  const page   = parseInt(searchParams.get('page') || '1');

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (status) params.status = status;
    API.get('/admin/voices', { params })
      .then(r => { setProfiles(r.data.profiles); setTotal(r.data.total); setPages(r.data.pages); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [status, page]);

  useEffect(() => { load(); }, [load]);

  const set = (key, val) => {
    const p = Object.fromEntries(searchParams);
    if (val) p[key] = val; else delete p[key];
    if (key !== 'page') p.page = '1';
    setSearchParams(p);
  };

  const deleteProfile = async (id) => {
    const ok = await confirm({ title: 'Delete voice profile?', message: 'This removes the profile from the database. You must also delete the voice model from the voice provider dashboard separately.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    setDeleting(id);
    try {
      await API.delete(`/admin/voices/${id}`);
      setMsg('Voice profile deleted from database.');
      load();
    } catch (e) {
      setMsg('Failed to delete.');
    } finally { setDeleting(null); }
  };

  return (
    <AdminLayout title="Voice Profiles">
      {/* Filters */}
      <div className="d-flex gap-2 flex-wrap align-items-center mb-4">
        {[
          { val: '',       label: 'All' },
          { val: 'ready',  label: '✅ Ready' },
          { val: 'failed', label: '❌ Failed' },
        ].map(tab => (
          <button key={tab.val} onClick={() => set('status', tab.val)}
            className={`btn btn-sm rounded-pill px-3 ${status === tab.val ? 'btn-dream' : 'btn-outline-secondary'}`}
            style={{ fontSize: '13px' }}>
            {tab.label}
          </button>
        ))}
        <span className="ms-auto text-muted small">{total} voice profiles</span>
      </div>

      {msg && (
        <div className="alert alert-info alert-dismissible py-2 small mb-3">
          {msg}
          <button type="button" className="btn-close btn-sm" onClick={() => setMsg('')}></button>
        </div>
      )}

      {/* Warning */}
      <div className="alert alert-warning py-2 small mb-4" style={{ borderRadius: '12px' }}>
        <i className="bi bi-exclamation-triangle me-2"></i>
        <strong>Note:</strong> Deleting a voice profile here removes it from the database only. You must also remove the voice model from the voice provider's dashboard to fully delete it.
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
        </div>
      ) : (
        <>
          <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead style={{ background: 'var(--page-bg)' }}>
                  <tr>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>USER</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>VOICE NAME</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>STATUS</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>PLAN</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>DURATION</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>CREATED</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}>PROVIDER ID</th>
                    <th style={{ fontSize: '12px', color: 'var(--text-muted2)', fontWeight: 600, padding: '12px 16px', border: 'none' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.length === 0 ? (
                    <tr><td colSpan={8} className="text-center text-muted py-5">No voice profiles found</td></tr>
                  ) : profiles.map(p => (
                    <tr key={p._id} style={{ verticalAlign: 'middle' }}>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                        {p.userId ? (
                          <Link to={`/admin/users/${p.userId._id}`} style={{ textDecoration: 'none' }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: '#6f42c1' }}>{p.userId.name}</div>
                            <div style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>{p.userId.email}</div>
                          </Link>
                        ) : (
                          <span className="text-muted small">Deleted user</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px', color: 'var(--text-main)' }}>
                        🎙️ {p.name}
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                        <span className={`badge bg-${p.status === 'ready' ? 'success' : 'danger'} bg-opacity-10 text-${p.status === 'ready' ? 'success' : 'danger'}`} style={{ fontSize: '11px' }}>
                          {p.status === 'ready' ? '✅ Ready' : '❌ Failed'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                        {p.userId?.subscription?.plan && (
                          <span className="badge rounded-pill" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '11px' }}>
                            {p.userId.subscription.plan}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '13px', color: 'var(--text-muted2)' }}>
                        {p.durationSecs ? `${p.durationSecs}s` : '—'}
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)', fontSize: '12px', color: 'var(--text-muted2)' }}>
                        {new Date(p.createdAt).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                        <code style={{ fontSize: '11px', color: 'var(--text-muted2)', background: 'var(--header-bg)', padding: '2px 6px', borderRadius: '4px' }}>
                          {p.elevenLabsVoiceId?.substring(0, 16)}…
                        </code>
                      </td>
                      <td style={{ padding: '12px 16px', border: 'none', borderBottom: '1px solid var(--row-sep)' }}>
                        <button
                          disabled={deleting === p._id}
                          onClick={() => deleteProfile(p._id)}
                          className="btn btn-sm btn-outline-danger rounded-pill px-2"
                          style={{ fontSize: '11px' }}>
                          {deleting === p._id
                            ? <span className="spinner-border spinner-border-sm"></span>
                            : <i className="bi bi-trash"></i>}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {pages > 1 && (
            <div className="d-flex justify-content-center gap-2 mt-4">
              <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page <= 1}
                onClick={() => set('page', String(page - 1))}>← Prev</button>
              <span className="btn btn-sm disabled">{page} / {pages}</span>
              <button className="btn btn-sm btn-outline-secondary rounded-pill" disabled={page >= pages}
                onClick={() => set('page', String(page + 1))}>Next →</button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default AdminVoiceProfiles;
